import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Check, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FIELD_ORDER = [
  ["person_name", "Person involved"],
  ["person_role", "Their role"],
  ["person_phone", "Phone"],
  ["incident_date", "Date"], ["incident_time", "Time"],
  ["incident_location", "Location"],
  ["incident_type", "Type"],
  ["incident_description", "What happened"],
  ["injury_body_part", "Injury — body part"],
  ["injury_severity", "Severity"],
  ["treatment_given", "Treatment given"],
  ["treatment_by", "Treated by"],
  ["hospital_attended", "Hospital attended"],
  ["contributing_factors", "Contributing factors"],
  ["corrective_actions", "Corrective actions"],
  ["employee_signature", "Employee signature"],
];

export default function ManagerIncidents() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [returning, setReturning] = useState(false);
  const [comments, setComments] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/manager/incidents", { params: { status: "PENDING_MANAGER" } });
      setRows(data.incidents || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/manager/incidents/${id}/approve`);
      toast.success("Approved · admin notified · filed in employee documents");
      setActive(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(false); }
  };

  const sendBack = async () => {
    if (!comments.trim()) { toast.error("Add comments explaining what to fix"); return; }
    setBusy(true);
    try {
      await api.post(`/manager/incidents/${active.id}/return`, { comments });
      toast.success("Returned to employee with comments");
      setReturning(false); setComments(""); setActive(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Return failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <h1 className="text-xl font-bold text-[#1A2744]">Pending incident records</h1>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Employee</TableHead><TableHead>Date</TableHead>
            <TableHead>Type</TableHead><TableHead>Site</TableHead><TableHead>Submitted</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-500 py-6">Nothing pending</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`incident-row-${r.id}`}>
                <TableCell className="text-sm">{r.user_name} <span className="text-gray-400 font-mono text-[11px] ml-1">{r.user_employee_code}</span></TableCell>
                <TableCell className="text-xs">{r.form?.incident_date || "—"}</TableCell>
                <TableCell className="text-xs">{r.form?.incident_type || "—"}</TableCell>
                <TableCell className="text-xs">{r.site}</TableCell>
                <TableCell className="text-xs text-gray-500">{r.submitted_at?.slice(0, 16).replace("T", " ")}</TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => setActive(r)}>Review</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {active && (
        <Dialog open={true} onOpenChange={(v) => { if (!v) { setActive(null); setReturning(false); setComments(""); } }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Incident · {active.user_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              {FIELD_ORDER.map(([k, label]) => {
                const v = active.form?.[k];
                if (v === undefined || v === null || v === "" || v === false) return null;
                return (
                  <div key={k} className="border-b border-gray-100 pb-1">
                    <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
                    <div className="text-sm break-words">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</div>
                  </div>
                );
              })}
            </div>
            {returning ? (
              <>
                <Textarea rows={4} placeholder="What needs fixing before re-submission?" value={comments} onChange={(e) => setComments(e.target.value)} data-testid="return-comments" />
                <DialogFooter>
                  <Button variant="outline" onClick={() => setReturning(false)} disabled={busy}>Cancel</Button>
                  <Button onClick={sendBack} disabled={busy} className="bg-amber-600 text-white" data-testid="return-confirm"><Undo2 className="h-3.5 w-3.5 mr-1" /> Send back</Button>
                </DialogFooter>
              </>
            ) : (
              <DialogFooter>
                <Button variant="outline" onClick={() => setReturning(true)} disabled={busy} data-testid="return-open"><Undo2 className="h-3.5 w-3.5 mr-1" /> Send back</Button>
                <Button onClick={() => approve(active.id)} disabled={busy} className="bg-emerald-600 text-white" data-testid="approve-confirm"><Check className="h-3.5 w-3.5 mr-1" /> Approve & file</Button>
              </DialogFooter>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
